import type { FeatureCollection } from 'geojson'
import { OddElementType } from '../../../graphql/src/lib/graphql/generated/types'

const CDN_BASE =
  'https://assets.test-mission-planning.int.moia-group.io/route-builder'
const BASE =
  window.location.hostname === 'localhost' ? '/route-builder-assets' : CDN_BASE

export type LayerConfig = {
  id: string
  label: string
  color: string
  type: 'geonet' | 'lanes'
  loader: () => Promise<FeatureCollection>
}

const fetchJson = async (path: string): Promise<FeatureCollection> => {
  try {
    const response = await fetch(`${BASE}/${path}`)
    return response.json()
  } catch {
    return { type: 'FeatureCollection', features: [] }
  }
}

/** Keyed by the mock-server's numeric metropolis IDs (1=Austin, 2=Munich, 3=Hamburg). */
export const LAYER_CONFIGS: Record<number, LayerConfig[]> = {
  1: [
    {
      id: 'austin-dev-geonet',
      label: 'Austin Development',
      color: '#00C8CC',
      type: 'geonet',
      loader: () => fetchJson('Q3-Dev-Austin-Geonet.json'),
    },
    {
      id: 'austin-cugt-geonet',
      label: 'Austin CUGT',
      color: '#f1c40f',
      type: 'geonet',
      loader: () => fetchJson('Austin_SA_CUGT_1.00_Planned.json'),
    },
    {
      id: 'austin-dev-lanes',
      label: 'Austin Dev Lanes',
      color: '#61cfd1',
      type: 'lanes',
      loader: () => fetchJson('june_ME_lanes.json'),
    },
    {
      id: 'austin-cugt-lanes',
      label: 'Austin CUGT Lanes',
      color: '#d9b00d',
      type: 'lanes',
      loader: () => fetchJson('ME_Coverage_CUGT.json'),
    },
  ],
  2: [
    {
      id: 'munich-dev-geonet',
      label: 'Munich Development',
      color: '#00C8CC',
      type: 'geonet',
      loader: () => fetchJson('munich_geonet.json'),
    },
    {
      id: 'munich-dev-lanes',
      label: 'Munich Lanes',
      color: '#d9b00d',
      type: 'lanes',
      loader: () => fetchJson('munich_lanes.json'),
    },
  ],
  3: [
    {
      id: 'hamburg-dev-geonet',
      label: 'Hamburg Development',
      color: '#00C8CC',
      type: 'geonet',
      loader: () => fetchJson('Q4_hamburg_poly.json'),
    },
    {
      id: 'hamburg-cugt-geonet',
      label: 'Hamburg CUGT',
      color: '#f1c40f',
      type: 'geonet',
      loader: () => fetchJson('hamburg_geonet.json'),
    },
    {
      id: 'hamburg-lanes',
      label: 'Hamburg Lanes',
      color: '#61cfd1',
      type: 'lanes',
      loader: () => fetchJson('hamburg_lanes.json'),
    },
  ],
  4: [
    {
      id: 'la-development-geonet',
      label: 'LA Development',
      color: '#00C8CC',
      type: 'geonet',
      loader: () => fetchJson('la_development_geonet.json'),
    },
    {
      id: 'la-production-geonet',
      label: 'LA Production',
      color: '#f1c40f',
      type: 'geonet',
      loader: () => fetchJson('la_production_geonet.json'),
    },
    {
      id: 'la-development-lanes',
      label: 'LA Development Lanes',
      color: '#61cfd1',
      type: 'lanes',
      loader: () => fetchJson('la_development_lanes.json'),
    },
    {
      id: 'la-production-lanes',
      label: 'LA Production Lanes',
      color: '#d9b00d',
      type: 'lanes',
      loader: () => fetchJson('la_production_lanes.json'),
    },
  ],
  5: [
    {
      id: 'lake-nona-geonet',
      label: 'Lake Nona',
      color: '#00C8CC',
      type: 'geonet',
      loader: () => fetchJson('lake_nona_geonet.json'),
    },
    {
      id: 'lake-nona-lanes',
      label: 'Lake Nona Lanes',
      color: '#61cfd1',
      type: 'lanes',
      loader: () => fetchJson('lake_nona_lanes.json'),
    },
  ],
}

export type OddToggleItem = { type: string; label: string; color: string }

export const ODD_TOGGLE_GROUPS: { section: string; items: OddToggleItem[] }[] =
  [
    {
      section: 'Maneuvers',
      items: [
        { type: 'PROTECTED_LEFT', label: 'Protected Left', color: '#2E7D32' },
        { type: 'PROTECTED_RIGHT', label: 'Protected Right', color: '#2E7D32' },
      ],
    },
    {
      section: 'Signs',
      items: [
        { type: 'STOP_SIGN', label: 'Stop', color: '#D32F2F' },
        { type: 'YIELD_SIGN', label: 'Yield', color: '#F9A825' },
        { type: 'NO_ENTRY', label: 'Do Not Enter', color: '#B71C1C' },
        {
          type: 'PEDESTRIAN_CROSSING',
          label: 'Pedestrian Crossing',
          color: '#0288D1',
        },
        {
          type: 'RAILROAD_CROSSING',
          label: 'Railroad Crossing',
          color: '#6D4C41',
        },
        { type: 'TRAFFIC_LIGHT', label: 'Traffic Light', color: '#455A64' },
        { type: 'NO_TURN_ON_RED', label: 'No Turn on Red', color: '#C62828' },
        { type: 'ONE_WAY', label: 'One Way', color: '#37474F' },
        { type: 'BUS_STOP', label: 'Bus Stop', color: '#0277BD' },
        { type: 'RIGHT_ON_RED', label: 'Right on Red', color: '#CF1020' },
      ],
    },
    {
      section: 'Zones',
      items: [
        { type: 'SCHOOL_ZONE', label: 'School Zone', color: '#F57F17' },
        { type: 'ROUNDABOUT', label: 'Roundabout', color: '#1976D2' },
      ],
    },
  ]

/**
 * Name-based fallback map — used when the API's numeric metropolis ID doesn't
 * match the mock-server IDs above. Matching is case-insensitive prefix/includes.
 */
const LAYER_CONFIGS_BY_NAME: Array<{ match: string; configs: LayerConfig[] }> =
  [
    { match: 'austin', configs: LAYER_CONFIGS[1] },
    { match: 'munich', configs: LAYER_CONFIGS[2] },
    { match: 'hamburg', configs: LAYER_CONFIGS[3] },
    { match: 'angeles', configs: LAYER_CONFIGS[4] },
    { match: 'nona', configs: LAYER_CONFIGS[5] },
  ]

/**
 * Resolve layer configs for a metropolis.
 * Name takes priority when provided — the numeric IDs only reliably map for
 * the local mock server and collide with real metro IDs in production.
 * Falls back to numeric ID only when no name is given.
 */
export const getLayerConfigs = (
  metropolisId: number,
  metropolisName?: string,
): LayerConfig[] => {
  if (metropolisName) {
    const lower = metropolisName.toLowerCase()
    return (
      LAYER_CONFIGS_BY_NAME.find(entry => lower.includes(entry.match))
        ?.configs ?? []
    )
  }
  return LAYER_CONFIGS[metropolisId] ?? []
}

type MetroBounds = {
  id: number
  name: string
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

// Approximate bounding boxes — generous enough to catch all service area polygons.
const METRO_BOUNDS: MetroBounds[] = [
  {
    id: 1,
    name: 'austin',
    minLat: 30.1,
    maxLat: 30.6,
    minLng: -97.95,
    maxLng: -97.45,
  },
  {
    id: 2,
    name: 'munich',
    minLat: 47.9,
    maxLat: 48.4,
    minLng: 11.2,
    maxLng: 11.9,
  },
  {
    id: 3,
    name: 'hamburg',
    minLat: 53.3,
    maxLat: 53.8,
    minLng: 9.6,
    maxLng: 10.4,
  },
  {
    id: 4,
    name: 'los angeles',
    minLat: 33.7,
    maxLat: 34.35,
    minLng: -118.7,
    maxLng: -118.1,
  },
  {
    id: 5,
    name: 'lake nona',
    minLat: 28.3,
    maxLat: 28.45,
    minLng: -81.4,
    maxLng: -81.2,
  },
]

/**
 * Infer which metro a set of coordinates belongs to by checking them against
 * known bounding boxes. Uses the centroid of all points to avoid mismatches
 * from sparse routes that span a boundary.
 *
 * Returns null when the centroid falls outside every known metro's bounds.
 */
export const inferMetroFromCoords = (
  coords: ReadonlyArray<{ latitude: number; longitude: number }>,
): { metropolisId: number; metropolisName: string } | null => {
  if (coords.length === 0) return null
  let latSum = 0
  let lngSum = 0
  for (const c of coords) {
    latSum += c.latitude
    lngSum += c.longitude
  }
  const lat = latSum / coords.length
  const lng = lngSum / coords.length
  const match = METRO_BOUNDS.find(
    m =>
      lat >= m.minLat && lat <= m.maxLat && lng >= m.minLng && lng <= m.maxLng,
  )
  return match ? { metropolisId: match.id, metropolisName: match.name } : null
}

/** Flat list derived from groups — kept for backward compatibility */
export const ODD_TOGGLE_TYPES: OddToggleItem[] = ODD_TOGGLE_GROUPS.flatMap(
  g => g.items,
)

/**
 * Maps our internal ODD type strings to the GraphQL OddElementType enum.
 * Types not in this map (e.g. PROTECTED_LEFT/RIGHT, MERGE) must fall back to
 * taxonomy-based filtering.
 */
export const ODD_TYPE_TO_ELEMENT_TYPE: Partial<Record<string, OddElementType>> =
  {
    STOP_SIGN: OddElementType.OddElementTypeStopSign,
    ALL_WAY_STOP: OddElementType.OddElementTypeStopSign,
    YIELD_SIGN: OddElementType.OddElementTypeYieldSign,
    NO_ENTRY: OddElementType.OddElementTypeNoEntry,
    PEDESTRIAN_CROSSING: OddElementType.OddElementTypePedestrianCrossing,
    RAILROAD_CROSSING: OddElementType.OddElementTypeRailroadCrossing,
    NO_TURN_ON_RED: OddElementType.OddElementTypeNoTurnOnRed,
    ONE_WAY: OddElementType.OddElementTypeOneWay,
    ROUNDABOUT: OddElementType.OddElementTypeRoundabout,
    TRAFFIC_LIGHT: OddElementType.OddElementTypeTrafficLight,
    BUS_STOP: OddElementType.OddElementTypeBusStop,
    SCHOOL_ZONE: OddElementType.OddElementTypeSchoolZone,
    RIGHT_ON_RED: OddElementType.OddElementTypeRightOnRed,
  }
